import React, { useState } from "react";
import "./CostSupportInfo.css";

type TabKey = "kotsu" | "faq" | "shikumi";

const ChevronDown = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

type AccordionItemProps = {
  id: string;
  badge: React.ReactNode;
  question: string;
  openIds: Set<string>;
  onToggle: (id: string) => void;
  children: React.ReactNode;
};

function AccordionItem({ id, badge, question, openIds, onToggle, children }: AccordionItemProps) {
  const isOpen = openIds.has(id);
  return (
    <div className={`csup-item${isOpen ? " csup-open" : ""}`}>
      <div className="csup-iq" onClick={() => onToggle(id)}>
        {badge}
        <span className="csup-iq-text">{question}</span>
        <span className="csup-chev">
          <ChevronDown />
        </span>
      </div>
      <div className="csup-ia">
        <div className="csup-ia-inner">{children}</div>
      </div>
    </div>
  );
}

export function CostSupportInfo() {
  const [activeTab, setActiveTab] = useState<TabKey>("kotsu");
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="csup-root" id="support-section">
      {/* セクションヘッダー */}
      <div className="csup-sec-header">
        <div className="csup-sec-icon">
          <svg viewBox="0 0 20 20">
            <path d="M10 2C5.6 2 2 5.6 2 10s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 13H9v-2h2v2zm0-4H9c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-1.65 1.35-3 3-3s3 1.35 3 3c0 2-3 2.25-3 5z" />
          </svg>
        </div>
        <span className="csup-sec-title">商品原価 サポート情報</span>
        <span className="csup-sec-date">最終更新：2025年4月</span>
      </div>

      {/* タブ */}
      <div className="csup-tabs">
        <button
          className={`csup-tab csup-tab-kotsu${activeTab === "kotsu" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("kotsu")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 11h12v1.5a1 1 0 01-1 1H3a1 1 0 01-1-1V11zm6-9.5a5 5 0 015 5c0 1.8-1 3.4-2.4 4.3L10 12H6l-.6-1.2A5 5 0 018 1.5zm1 2.3V5h1.5v1.5H9V8H7.5V6.5H6V5h1.5V3.8h1.5z" />
          </svg>
          運用のコツ
        </button>
        <button
          className={`csup-tab csup-tab-faq${activeTab === "faq" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("faq")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 2H2a1 1 0 00-1 1v8a1 1 0 001 1h2v2.5l3-2.5h7a1 1 0 001-1V3a1 1 0 00-1-1zM8 9.5a1 1 0 110-2 1 1 0 010 2zm1-3.3c0 .4-.2.7-.5.9L8 7.4V7h-.5V5.8l.8-.5c.2-.1.2-.2.2-.3 0-.3-.2-.5-.5-.5s-.5.2-.5.5H6c0-.9.7-1.5 1.5-1.5S9 5.3 9 6.2z" />
          </svg>
          よくある質問
        </button>
        <button
          className={`csup-tab csup-tab-shikumi${activeTab === "shikumi" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("shikumi")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 11.5a1 1 0 110-2 1 1 0 010 2zm.7-4.2c-.4.3-.7.6-.7 1v.2H6.5v-.2c0-.9.5-1.6 1.1-2 .4-.3.6-.6.6-1 0-.6-.5-1-1.2-1-.6 0-1.1.4-1.2.9H4.3C4.5 4.8 5.6 4 7 4c1.6 0 2.7 1 2.7 2.3 0 .9-.5 1.6-1 2z" />
          </svg>
          仕組みを知る
        </button>
      </div>

      {/* 運用のコツ */}
      <div className={`csup-panel${activeTab === "kotsu" ? " csup-active" : ""}`}>
        <AccordionItem
          id="kotsu-1"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="まず何から原価を入れればいいですか？優先順位の考え方"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>全商品を一度に入力しようとすると手が止まりがちです。以下の順番で進めると、短時間で利益数値の精度を高められます。</p>
          </div>
          <ul className="csup-slist">
            <li><span className="csup-snum csup-sn-g">1</span><span>売上上位20商品の原価を入力する（全体売上の大半をカバーできる）</span></li>
            <li><span className="csup-snum csup-sn-g">2</span><span>利益率が高い・低いと感じている商品を追加する（感覚値の検証に使える）</span></li>
            <li><span className="csup-snum csup-sn-g">3</span><span>新規登録商品は出品時に同時入力するルールを作る</span></li>
            <li><span className="csup-snum csup-sn-g">4</span><span>残りの商品はCSV一括インポートでまとめて登録する</span></li>
          </ul>
        </AccordionItem>

        <AccordionItem
          id="kotsu-2"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="原価に「何を含めるか」で利益の見え方が変わります"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>「原価」に何を含めるかは店舗によって異なります。社内でルールを統一することが重要です。</p>
          </div>
          <table className="csup-ftable">
            <thead>
              <tr>
                <th>方針</th>
                <th>原価に含めるもの</th>
                <th>特徴</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>仕入れ原価のみ</td>
                <td>商品の購入単価だけ</td>
                <td>シンプルで入力しやすい。梱包・人件費は固定費入力で別管理</td>
              </tr>
              <tr>
                <td>直接原価込み</td>
                <td>購入単価＋梱包材費＋同梱物</td>
                <td>商品ごとのコストをより正確に把握できる</td>
              </tr>
            </tbody>
          </table>
          <div className="csup-note csup-note-green">
            迷ったら「仕入れ原価のみ」から始めて、梱包費は固定費入力に月額で登録する方法がおすすめです。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-3"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="商品数が多い場合はCSV一括インポートが便利です"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>テンプレートCSVをダウンロードし「商品管理番号」「SKU」「原価」の3列を入力してアップロードするだけで一括登録できます。インポート後はプレビューで内容を確認してから確定できます。</p>
          </div>
          <div className="csup-note csup-note-blue">
            CSVテンプレートは商品原価画面右上の「CSVインポート」ボタンからダウンロードできます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-4"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="定期的な原価見直しのタイミングと方法"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>仕入れ価格は為替・仕入れ先の値上げ等で変動します。原価が古いままだと利益の数字がずれていきます。以下のタイミングで見直すと精度を保てます。</p>
          </div>
          <ul className="csup-slist">
            <li><span className="csup-snum csup-sn-g">月</span><span>仕入れ値が変わった商品を随時更新する</span></li>
            <li><span className="csup-snum csup-sn-g">季</span><span>四半期ごとに全商品の原価を棚卸しする</span></li>
            <li><span className="csup-snum csup-sn-g">年</span><span>年次の利益振り返り前に全件チェックする</span></li>
          </ul>
        </AccordionItem>
      </div>

      {/* よくある質問 */}
      <div className={`csup-panel${activeTab === "faq" ? " csup-active" : ""}`}>
        <AccordionItem
          id="faq-1"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="スーパーセールで商品が半額になった場合、原価はどう設定すればいいですか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>原価は販売価格の変動に関わらず、実際の仕入れ原価（購入単価）をそのまま入力してください。半額セール時の利益率の変動は、利益Naviが自動で計算します。</p>
            <p>たとえば仕入れ原価500円の商品を通常1,200円で販売し、スーパーセール中に600円で販売した場合、原価設定は「500円」のままで問題ありません。</p>
          </div>
          <div className="csup-note csup-note-blue">
            セール期間中の売上・利益の変化は「期間利益」メニューの日付フィルタで確認できます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-2"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="SKUごとに原価を入力しなければいけませんか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>SKUごとの入力は任意です。未入力の場合は商品管理番号に設定した原価が自動適用されます。</p>
          </div>
          <table className="csup-ftable">
            <thead>
              <tr>
                <th>入力パターン</th>
                <th>適用される原価</th>
                <th>おすすめシーン</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>管理番号のみ</td>
                <td>全SKUに同一原価を適用</td>
                <td>カラー・サイズで原価が同じ商品</td>
              </tr>
              <tr>
                <td>SKUごとに入力</td>
                <td>各SKUに個別原価を適用</td>
                <td>サイズで仕入れ値が異なる場合</td>
              </tr>
              <tr>
                <td>一部のSKUのみ</td>
                <td>入力済みは個別、未入力は管理番号の原価</td>
                <td>主要サイズのみ原価が異なる場合</td>
              </tr>
            </tbody>
          </table>
        </AccordionItem>

        <AccordionItem
          id="faq-3"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="原価を変更したら、過去の利益データも変わりますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>はい、原価を変更すると過去の売上データに遡って再計算が反映されます。仕入れ価格の見直しや入力ミスの訂正を行った際は、月次・日次利益の数値も更新されます。</p>
          </div>
          <div className="csup-note csup-note-amber">
            意図せず過去データが変わる場合があります。仕入れ価格が変わった際は変更タイミングをメモしておくことを推奨します。
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-4"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="原価が0円（未設定）のまま放置すると、利益はどう表示されますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>原価が未設定（0円）の場合、原価0円として計算されるため利益が実際より高く表示されます。売上が発生している商品には必ず原価を入力してください。</p>
          </div>
        </AccordionItem>
      </div>

      {/* 仕組みを知る */}
      <div className={`csup-panel${activeTab === "shikumi" ? " csup-active" : ""}`}>
        <AccordionItem
          id="shikumi-1"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="利益はどのように計算されていますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>利益Naviは以下の計算式をベースに、注文データと各種コストを自動で突合して利益を算出しています。</p>
          </div>
          <div className="csup-formula">
            <span>利益</span> ＝ 売上 − 原価 − 楽天手数料 − 送料 − 広告費 − 固定費（月割）
          </div>
          <table className="csup-ftable" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>コスト項目</th>
                <th>データの取得方法</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>原価</td><td>この画面で入力した値を使用</td></tr>
              <tr><td>楽天手数料</td><td>契約プランに応じて自動計算</td></tr>
              <tr><td>送料</td><td>初期設定で登録した送料テーブルを参照</td></tr>
              <tr><td>広告費</td><td>楽天RMS連携データ（RPP・クーポン等）</td></tr>
              <tr><td>固定費</td><td>固定費入力で登録した月額費用を日割り配分</td></tr>
            </tbody>
          </table>
        </AccordionItem>

        <AccordionItem
          id="shikumi-2"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="原価の照合はどのように行われていますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>注文データが届くと「商品管理番号」と「SKU」の2つのキーを使って、登録済みの原価を自動で割り当てます。SKUの原価が未登録の場合は、商品管理番号の原価で補完されます。</p>
          </div>
          <div className="csup-note csup-note-blue">
            以前は4キー（管理番号・SKU・販売期間・店舗ID）で照合していましたが、運用の簡素化のため現在は2キー照合に変更されています。
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-3"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="楽天手数料はどのように計算されていますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>契約プラン（がんばれ！楽天市場・スタンダード・メガショップ等）に応じた販売手数料率を売上金額に乗じて自動計算しています。ポイント原資・アフィリエイト報酬・楽天ペイ手数料なども含めた実態に近い手数料を反映しています。</p>
          </div>
          <div className="csup-note csup-note-blue">
            プランの変更があった場合は初期設定から契約プランを更新してください。
          </div>
        </AccordionItem>
      </div>
    </div>
  );
}
